import React from "react";
import { Routes, Route, useNavigate, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom";

// MSAL imports
import { MsalProvider } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { CustomNavigationClient } from "./utils/NavigationClient";

// Pages
import Layout from './components/Layout'
import { Dashboard, FileSubmissions, PBMList, PBMAdd, PBMDetail, TransactionList, TransactionAdd, TransactionDetail, EligibilityList, EligibilityDetail, EligibilityAdd, FileOutputLogs, DataPipelineDemo, SignedOut, UserManagement, Files } from './screens'

// Sentry
import * as Sentry from "@sentry/react";


Sentry.init({
    dsn: "https://50e0d32fcbfc28598c2d98ca46f934d6@o4508813827768320.ingest.us.sentry.io/4508813896122368",
    integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
    ],
    environment: import.meta.env.MODE,
    // Disable Sentry in development mode to reduce spans
    enabled: import.meta.env.MODE !== 'development',
    // Tracing
    tracesSampleRate: import.meta.env.MODE === 'development' ? 0 : 1.0, // Capture 0% in dev, 100% in prod
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/nash-app-prod-dfhaa2bzf9gfapd5\.eastus-01\.azurewebsites\.net\/api/],
    // Session Replay
    //replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

type AppProps = {
    pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
    // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    return (
        <MsalProvider instance={pca}>
            <Layout>
            <SentryRoutes>
                {/* Public routes */}
                <Route path="/signed-out" element={<SignedOut />} />
                
                {/* Protected routes */}
                
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/file-submissions" element={<FileSubmissions />} />
                    <Route path="/pbm" element={<PBMList />} />
                    <Route path="/pbm/add" element={<PBMAdd />} />
                    <Route path="/pbm/:id" element={<PBMDetail />} />
                    <Route path="/transactions" element={<TransactionList />} />
                    <Route path="transaction/add" element={<TransactionAdd />} />
                    <Route path="/transaction/:transactionId" element={<TransactionDetail />} />
                    <Route path="/eligibility" element={<EligibilityList />} />
                    <Route path="/eligibility/add" element={<EligibilityAdd />} />
                    <Route path="/eligibility/:eligibilityId" element={<EligibilityDetail />} />
                    <Route path="/output-logs" element={<FileOutputLogs />} />
                    <Route path="/files" element={<Files />} />
                    <Route path="/pipeline-demo" element={<DataPipelineDemo />} />
                    <Route path="/users" element={<UserManagement />} />

            </SentryRoutes>
            </Layout>
        </MsalProvider>
    );
}

export default App;
